import React from "react";
import ProviderProductPriceTiers from "../components/ProviderProductPriceTiers";
import { PRODUCT_BOOLEAN_OPTIONS, emptyQuantityTier } from "../constants/dropshippingInterneConstants";
import { useDropshippingText } from "../i18n";
import "./ProviderCommercialSections.css";
import { elakayAutoText } from "../../pageInside/dataElakay";


// Sections "Wholesale & tiers", "Dropshipping/storage/shipping" et "Geographic
// zones" du Provider Product -- partagées entre ProviderProductForm (Edit) et
// ProviderProductWizard (Add, étape Info) pour ne jamais laisser les deux
// définitions diverger. `t(key, fallback)` optionnel : l'Edit garde ses libellés
// anglais, le wizard passe son traducteur (namespace providerProductWizard).

// Les libellés de PRODUCT_BOOLEAN_OPTIONS sont "Titre (explication)" : on les
// sépare pour l'affichage en carte, sans toucher aux constantes ni aux clés.
const splitLabel = (label) => {
  const match = /^(.*?)\s*\((.*)\)\s*$/.exec(label);
  return match ? { title: match[1], hint: match[2] } : { title: label, hint: "" };
};

// Case à cocher réelle (clavier + lecteurs d'écran) habillée en interrupteur.
// pointer-events/position sont forcés dans le CSS : voir "mdbreact checkbox trap".
const SwitchCard = ({ checked, onChange, title, hint }) => (
  <label className={`pcs-card${checked ? " is-on" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="pcs-switch" aria-hidden="true" />
    <span className="pcs-card__text">
      <strong>{title}</strong>
      {hint && <small>{hint}</small>}
    </span>
  </label>
);

const parseCountries = (value) =>
  [...new Set(String(value || "").split(",").map((s) => s.trim().toUpperCase()).filter(Boolean))];

// `show` : quelles sections afficher (Add les affiche toutes ; Edit les répartit
// dans ses onglets Pricing / Dropshipping & Fulfillment).
const ALL_SECTIONS = ["pricing", "fulfillment", "zones"];

// Un frais configurable : non défini (le Seller le verra « à confirmer »), gratuit, ou facturé (montant + flat/par unité).
const FeeEditor = ({ id, label, fee, onChange, ct }) => (
  <fieldset className="pcs-fee">
    <legend>{label}</legend>
    <div className="pcs-fee__row">
      <select aria-label={label} value={fee.mode} onChange={(e) => onChange({ ...fee, mode: e.target.value })}>
        <option value="unset">{ct("feeUnset")}</option><option value="free">{ct("feeFree")}</option><option value="charged">{ct("feeCharged")}</option>
      </select>
      {fee.mode === "charged" && (<>
        <input type="number" min="0" step="0.01" aria-label={`${label} — ${ct("feeAmount")}`} placeholder={ct("feeAmount")} value={fee.amount} onChange={(e) => onChange({ ...fee, amount: e.target.value })} />
        <select aria-label={elakayAutoText("h_c9784601db2599", "{value0} — type", { value0: label })} value={fee.type} onChange={(e) => onChange({ ...fee, type: e.target.value })}>
          <option value="per_unit">{ct("feePerUnit")}</option><option value="flat">{ct("feeFlat")}</option>
        </select>
      </>)}
    </div>
    {fee.mode === "unset" && <small className="pcs-note">{ct("unsetWarning")}</small>}
  </fieldset>
);

const ProviderCommercialSections = ({ form, setField, setForm, t = (key, fallback) => fallback, show = ALL_SECTIONS }) => {
  const ct = useDropshippingText(null, "dropshippingCommercial");
  const setFee = (key) => (fee) => setForm((prev) => ({ ...prev, fees: { ...prev.fees, [key]: fee } }));
  const countries = parseCountries(form.allowedCountries);
  const paymentRules = form.dropshippingPayment || {};
  const paymentFee = paymentRules.sellerPaymentAcceptanceFee || { enabled: false, type: "none", value: "" };
  const setPayment = (patch) => setForm((prev) => {
    const currentPayment = prev.dropshippingPayment || {};
    const currentFee = currentPayment.sellerPaymentAcceptanceFee || { enabled: false, type: "none", value: "" };
    const nextFee = { ...currentFee, ...(patch.sellerPaymentAcceptanceFee || {}) };
    if (nextFee.type === "none") nextFee.value = 0;
    return {
      ...prev,
      dropshippingPayment: {
        ...currentPayment,
        ...patch,
        sellerPaymentAcceptanceFee: nextFee,
      },
    };
  });
  const paymentFeeType = paymentFee.type || "none";
  return (
    <div className="pcs">
      {show.includes("pricing") && (
      <section className="pcs-section">
        <header className="pcs-section__head">
          <h2>{t("t006", "Wholesale & quantity tiers")}</h2>
        </header>
        <SwitchCard
          checked={form.wholesaleEnabled}
          onChange={setField("wholesaleEnabled")}
          title={t("t021", "Wholesale enabled")}
        />
        <label className="pcs-field">
          <span>{t("t020", "Minimum order quantity")}</span>
          <input type="number" min="1" value={form.minimumOrderQuantity} onChange={setField("minimumOrderQuantity")} />
        </label>
        {/* Prix Simple Dropshipping : indépendant du prix d'achat (base + paliers) -- jamais un prix unique pour tous les modes. */}
        <div className="pcs-field">
          <label>
            <span>{ct("dsPriceTitle")}</span>
            <input type="number" min="0" step="0.01" value={form.dropshippingPrice} onChange={setField("dropshippingPrice")} />
          </label>
          <small>{ct("dsPriceHint")}</small>
        </div>
        {form.wholesaleEnabled && <p className="pcs-note">{ct("purchasedPriceNote")}</p>}
        {form.wholesaleEnabled && (
          <div className="pcs-grid">
            <label className="pcs-field"><span>{ct("minStore")}</span><input type="number" min="1" value={form.purchaseMinStore} onChange={setField("purchaseMinStore")} /></label>
            <label className="pcs-field"><span>{ct("minShip")}</span><input type="number" min="1" value={form.purchaseMinShip} onChange={setField("purchaseMinShip")} /></label>
            <small className="pcs-note">{ct("minHint")}</small>
          </div>
        )}
        {form.wholesaleEnabled && (
          <ProviderProductPriceTiers
            tiers={form.quantityPricing}
            onChange={(tiers) => setForm((prev) => ({ ...prev, quantityPricing: tiers }))}
          />
        )}
        {form.wholesaleEnabled && form.quantityPricing.length === 0 && (
          <button
            type="button"
            className="provider-price-tiers__add pcs-add"
            onClick={() => setForm((prev) => ({ ...prev, quantityPricing: [emptyQuantityTier()] }))}
          >
            {t("t022", "+ Add your first quantity tier")}
          </button>
        )}
      </section>
      )}

      {show.includes("fulfillment") && (
      <section className="pcs-section">
        <header className="pcs-section__head">
          <h2>{t("t007", "Dropshipping, storage & shipping")}</h2>
        </header>
        <div className="pcs-grid">
          {PRODUCT_BOOLEAN_OPTIONS.map(({ key, label }) => {
            const { title, hint } = splitLabel(label);
            return <SwitchCard key={key} checked={form[key]} onChange={setField(key)} title={title} hint={hint} />;
          })}
        </div>
        <header className="pcs-section__head"><h3>{ct("feesTitle")}</h3></header>
        <SwitchCard checked={Boolean(form.logisticsEnabled)} onChange={setField("logisticsEnabled")} title={ct("logisticsEnabled")} />
        {form.logisticsEnabled && (<>
          <FeeEditor label={ct("feeDropLogistics")} fee={form.fees.dropshippingLogistics} onChange={setFee("dropshippingLogistics")} ct={ct} />
          <FeeEditor label={ct("feePurchasedLogistics")} fee={form.fees.purchasedStockLogistics} onChange={setFee("purchasedStockLogistics")} ct={ct} />
          <SwitchCard checked={Boolean(form.purchasedFallsBack)} onChange={setField("purchasedFallsBack")} title={ct("fallbackToDs")} />
        </>)}
        {form.providerStorageEnabled && <FeeEditor label={ct("feeStorage")} fee={form.fees.storage} onChange={setFee("storage")} ct={ct} />}
        {form.shippingByProviderEnabled && <FeeEditor label={ct("feeShipping")} fee={form.fees.shipping} onChange={setFee("shipping")} ct={ct} />}
        <section className="pcs-section pcs-payment-acceptance">
          <header className="pcs-section__head"><h3>{t("paymentAcceptanceTitle", "Save time with payment acceptance")}</h3></header>
          <p className="pcs-payment-acceptance__purpose">{elakayAutoText("h_payment_provider_offer_purpose", "Offer to accept orders and capture authorized customer payments on the Seller's behalf. Each Seller chooses whether to use this service or manage their own payments.")}</p>
          <SwitchCard
            checked={Boolean(paymentRules.sellerPaymentAcceptanceAllowed)}
            onChange={(e) => setPayment({ sellerPaymentAcceptanceAllowed: e.target.checked })}
            title={t("paymentAcceptanceAllowed", "Offer Payment Acceptance Service to Sellers")}
            hint={t("paymentAcceptanceHint", "Allow Sellers to delegate payment acceptance and capture to you for their Dropshipping In orders. You can charge a service fee for handling payments on their behalf. Sellers remain free to decline this service and manage their own payments.")}
          />
          {paymentRules.sellerPaymentAcceptanceAllowed && (
            <fieldset className="pcs-fee">
              <legend>{t("paymentAcceptanceFeeTitle", "Payment acceptance service fee")}</legend>
              <div className="pcs-fee__row">
                <select value={paymentFeeType} onChange={(e) => setPayment({ sellerPaymentAcceptanceFee: { type: e.target.value, enabled: e.target.value !== "none" } })} aria-label={elakayAutoText("h_529975a52fadf5", "Payment acceptance fee type")}>
                  <option value="none">{t("paymentAcceptanceNoFee", "No fee")}</option>
                  <option value="fixed">{t("paymentAcceptanceFixed", "Fixed amount")}</option>
                  <option value="percentage">{t("paymentAcceptancePercentage", "Percentage")}</option>
                </select>
                {paymentFeeType === "fixed" && (
                  <input type="number" min="0" max="0.75" step="0.01" value={paymentFee.value} onChange={(e) => setPayment({ sellerPaymentAcceptanceFee: { value: e.target.value } })} aria-label={elakayAutoText("h_c30fac40f8967a", "Fixed payment acceptance fee")} />
                )}
                {paymentFeeType === "percentage" && (
                  <input type="number" min="0" max="100" step="0.01" value={paymentFee.value} onChange={(e) => setPayment({ sellerPaymentAcceptanceFee: { value: e.target.value } })} aria-label={elakayAutoText("h_16f6d26639a2bc", "Percentage payment acceptance fee")} />
                )}
              </div>
              {paymentFeeType === "fixed" && <small className="pcs-note">{t("paymentAcceptanceFixedLimit", "Maximum allowed fixed fee: $0.75")}</small>}
              {paymentFeeType === "percentage" && <small className="pcs-note">{t("paymentAcceptancePercentageHint", "The percentage is calculated from the authorized customer payment amount.")}</small>}
              {paymentFeeType !== "percentage" && <small className="pcs-note">{elakayAutoText("h_payment_provider_fee_hint", "This service fee applies when a Seller delegates payment acceptance to you. The maximum fixed fee is $0.75.")}</small>}
            </fieldset>
          )}
        </section>
      </section>
      )}

      {show.includes("zones") && (
      <section className="pcs-section">
        <header className="pcs-section__head">
          <h2>{t("t008", "Geographic zones")}</h2>
        </header>
        <label className="pcs-field">
          <span>{t("t009", "Allowed countries (ISO codes, comma-separated, e.g. US, CA)")}</span>
          <input type="text" value={form.allowedCountries} onChange={setField("allowedCountries")} placeholder={elakayAutoText("h_b9d5178db63f26", "US, CA")} />
        </label>
        {countries.length > 0 && (
          <ul className="pcs-chips" aria-label={elakayAutoText("h_f1d266b18c4480", "Selected countries")}>
            {countries.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
        )}
        <p className={`pcs-note${countries.length === 0 ? " is-warning" : ""}`}>
          {t("t010", "No country is allowed by default: this product won't be usable until at least one is added.")}
        </p>
      </section>
      )}
    </div>
  );
};

export default ProviderCommercialSections;
